import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/system';
import { VALET, FONTS, TYPE } from '../../theme';
import { MOBILE } from './responsive';

const SectionTitle = styled(Typography)({
  ...TYPE.sectionTitle,
  marginBottom: '4px',
});

const Caption = styled(Typography)({
  fontFamily: FONTS.sans,
  fontSize: '13px',
  color: VALET.textMuted,
  marginBottom: '16px',
});

const TableWrapper = styled(Box)({
  backgroundColor: VALET.surface,
  border: `1px solid ${VALET.border}`,
  borderRadius: '14px',
  overflow: 'hidden',
  boxShadow: VALET.shadow,
});

// The table is wider than the content column on smaller screens; scroll it
// inside its own box rather than letting the whole dashboard slide sideways.
const Scroller = styled(Box)({
  overflowX: 'auto',
});

// Eleven columns is roughly three phone screens of sideways dragging, so each
// request becomes a card: what was ordered and for how much, who ordered it and
// their number, then the three pills that say how it went, then where and when.
const RequestCard = styled(Box)({
  padding: '14px',
  borderBottom: `1px solid ${VALET.border}`,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  '&:last-of-type': { borderBottom: 'none' },
});

const CardTopLine = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '10px',
});

const CardService = styled(Typography)({
  fontFamily: FONTS.sans,
  fontSize: '15px',
  fontWeight: 600,
  color: VALET.textPrimary,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  minWidth: 0,
});

const CardAmount = styled(Typography)({
  fontFamily: FONTS.mono,
  fontSize: '14px',
  fontWeight: 600,
  color: VALET.textPrimary,
  flexShrink: 0,
});

const CardWho = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '8px',
  flexWrap: 'wrap',
  fontFamily: FONTS.sans,
  fontSize: '13px',
  color: VALET.textSecondary,
});

const CardPhone = styled('a')({
  fontFamily: FONTS.mono,
  fontSize: '13px',
  color: VALET.green,
  textDecoration: 'none',
});

const CardPills = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  flexWrap: 'wrap',
});

const CardFoot = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: '10px',
  flexWrap: 'wrap',
  fontFamily: FONTS.mono,
  fontSize: '11.5px',
  color: VALET.textMuted,
});

const CardAddress = styled(Typography)({
  fontFamily: FONTS.sans,
  fontSize: '12.5px',
  color: VALET.textMuted,
  lineHeight: 1.4,
});

const EmptyNote = styled(Typography)({
  fontFamily: FONTS.sans,
  fontSize: '13px',
  color: VALET.textMuted,
  textAlign: 'center',
  padding: '32px 16px',
});

const StyledHeaderCell = styled(TableCell)({
  fontFamily: FONTS.mono,
  fontSize: '10px',
  color: VALET.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.16em',
  fontWeight: 500,
  padding: '14px 16px',
  '@media (max-width: 768px)': { padding: '10px 12px' },
  borderBottom: `1px solid ${VALET.border}`,
  backgroundColor: VALET.surfaceMuted,
  whiteSpace: 'nowrap',
});

const StyledCell = styled(TableCell)({
  padding: '12px 16px',
  '@media (max-width: 768px)': { padding: '10px 12px' },
  fontSize: '13px',
  fontFamily: FONTS.sans,
  color: VALET.textPrimary,
  borderBottom: `1px solid ${VALET.border}`,
  verticalAlign: 'top',
});

const FilterChip = styled(Box)(({ selected }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '3px 10px',
  borderRadius: '10px',
  fontSize: '11px',
  cursor: 'pointer',
  border: '0.5px solid',
  borderColor: selected ? '#1a1a1a' : '#e0ddd6',
  backgroundColor: selected ? '#1a1a1a' : '#ffffff',
  color: selected ? '#ffffff' : VALET.textMuted,
  userSelect: 'none',
  whiteSpace: 'nowrap',
}));

const Pill = styled(Box)(({ tone }) => {
  const tones = {
    good: { bg: VALET.successBg, fg: VALET.success },
    bad: { bg: VALET.dangerBg, fg: VALET.danger },
    warn: { bg: VALET.warningBg, fg: VALET.warning },
    flat: { bg: VALET.surfaceMuted, fg: VALET.textMuted },
  };
  const t = tones[tone] || tones.flat;
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'capitalize',
    backgroundColor: t.bg,
    color: t.fg,
    whiteSpace: 'nowrap',
  };
});

const StatBox = styled(Box)({
  border: `1px solid ${VALET.border}`,
  borderRadius: '12px',
  backgroundColor: VALET.surface,
  padding: '12px 14px',
  minWidth: '124px',
});

// How far the customer got. 'abandoned' is the one that costs money: a card
// form was ready and no card was ever submitted.
const reachedTone = (r) =>
  r === 'paid' ? 'good' : r === 'declined' ? 'bad' : r === 'abandoned' ? 'warn' : 'flat';

const statusTone = (s) =>
  s === 'completed' ? 'good' : s === 'cancelled' ? 'bad' : s === 'parked' ? 'warn' : 'flat';

const fmtWhen = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'parked', label: 'Parked now' },
  { key: 'unpaged', label: 'No valet alerted' },
];

export default function ServicesTab({ data }) {
  const isMobile = useMediaQuery(MOBILE.replace('@media ', ''));
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const counts = useMemo(() => {
    const c = { all: rows.length, completed: 0, cancelled: 0, parked: 0, unpaged: 0 };
    rows.forEach((r) => {
      if (c[r.status] !== undefined) c[r.status] += 1;
      if (!r.valetsPaged) c.unpaged += 1;
    });
    return c;
  }, [rows]);

  const collected = useMemo(
    () => rows.filter((r) => r.paymentStatus === 'paid').reduce((sum, r) => sum + (r.amount || 0), 0),
    [rows]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const passesFilter =
        filter === 'all'
          ? true
          : filter === 'unpaged'
            ? !r.valetsPaged
            : r.status === filter;
      if (!passesFilter) return false;
      if (!q) return true;
      return (
        (r.customerName || '').toLowerCase().includes(q) ||
        (r.customerPhone || '').toLowerCase().includes(q) ||
        (r.address || '').toLowerCase().includes(q) ||
        (r.service || '').toLowerCase().includes(q) ||
        (r.valetName || '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, query]);

  if (!Array.isArray(data)) {
    return <Caption>Loading service requests…</Caption>;
  }

  return (
    <Box>
      <SectionTitle>Service requests</SectionTitle>
      <Caption>
        Every booking ever created, newest first. Times are New York.
      </Caption>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '18px' }}>
        <StatBox>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', color: VALET.textMuted }}>
            Requests
          </Typography>
          <Typography sx={{ fontFamily: FONTS.sans, fontSize: '22px', fontWeight: 700, color: VALET.textPrimary }}>
            {counts.all}
          </Typography>
        </StatBox>
        <StatBox>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', color: VALET.textMuted }}>
            Completed
          </Typography>
          <Typography sx={{ fontFamily: FONTS.sans, fontSize: '22px', fontWeight: 700, color: VALET.success }}>
            {counts.completed}
          </Typography>
        </StatBox>
        <StatBox>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', color: VALET.textMuted }}>
            Parked now
          </Typography>
          <Typography sx={{ fontFamily: FONTS.sans, fontSize: '22px', fontWeight: 700, color: VALET.warning }}>
            {counts.parked}
          </Typography>
        </StatBox>
        <Tooltip
          arrow
          placement="top"
          title="A valet is only alerted after payment succeeds, and the customer's app makes that call — not the backend. So an abandoned checkout leaves a request here that no valet was ever told about."
        >
          <StatBox sx={{ cursor: 'help' }}>
            <Typography sx={{ fontFamily: FONTS.mono, fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', color: VALET.textMuted }}>
              No valet alerted
            </Typography>
            <Typography sx={{ fontFamily: FONTS.sans, fontSize: '22px', fontWeight: 700, color: VALET.danger }}>
              {counts.unpaged}
            </Typography>
          </StatBox>
        </Tooltip>
        <StatBox>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: '10px', letterSpacing: '0.14em', textTransform: 'uppercase', color: VALET.textMuted }}>
            Collected
          </Typography>
          <Typography sx={{ fontFamily: FONTS.sans, fontSize: '22px', fontWeight: 700, color: VALET.textPrimary }}>
            ${collected.toFixed(2)}
          </Typography>
        </StatBox>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        {FILTERS.map((f) => (
          <FilterChip
            key={f.key}
            selected={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label} {counts[f.key] !== undefined ? counts[f.key] : ''}
          </FilterChip>
        ))}
        <TextField
          size="small"
          placeholder="Search name, phone, address, valet…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{
            // Pushed to the right of the filter chips on a desktop; on a phone
            // `marginLeft: auto` left it stranded half off the row, so it takes
            // the whole width instead.
            marginLeft: 'auto',
            minWidth: '260px',
            [MOBILE]: {
              marginLeft: 0,
              minWidth: 0,
              width: '100%',
            },
            '& .MuiOutlinedInput-root': {
              fontFamily: FONTS.sans,
              fontSize: '13px',
              borderRadius: '10px',
              backgroundColor: VALET.surface,
            },
          }}
        />
      </Box>

      <TableWrapper>
        {isMobile ? (
          <Box>
            {visible.map((r) => (
              <RequestCard key={r._id}>
                <CardTopLine>
                  <CardService>
                    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.service}
                    </Box>
                    {r.isFreeService ? <Pill tone="flat">free</Pill> : null}
                  </CardService>
                  <CardAmount>{r.amount ? `$${r.amount.toFixed(2)}` : '—'}</CardAmount>
                </CardTopLine>

                <CardWho>
                  <Box component="span" sx={{ fontWeight: 600, color: VALET.textPrimary }}>
                    {r.customerName}
                  </Box>
                  {r.customerPhone ? (
                    <CardPhone href={`tel:${String(r.customerPhone).replace(/[^\d+]/g, '')}`}>
                      {r.customerPhone}
                    </CardPhone>
                  ) : null}
                </CardWho>

                <CardPills>
                  <Pill tone={statusTone(r.status)}>{r.status}</Pill>
                  <Pill tone={r.paymentStatus === 'paid' ? 'good' : 'bad'}>{r.paymentStatus}</Pill>
                  {r.reached ? <Pill tone={reachedTone(r.reached)}>{r.reached}</Pill> : null}
                </CardPills>

                {r.address ? <CardAddress>{r.address}</CardAddress> : null}

                <CardFoot>
                  <span>{fmtWhen(r.createdAt)}</span>
                  <span>{r.valetName || 'no valet'}</span>
                  {/* Zero valets alerted is the failure this table exists to catch. */}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: r.valetsPaged ? VALET.textMuted : VALET.danger }}
                  >
                    {r.valetsPaged} alerted
                  </Box>
                </CardFoot>
              </RequestCard>
            ))}
            {visible.length === 0 && <EmptyNote>No requests match that.</EmptyNote>}
          </Box>
        ) : (
        <Scroller>
          <Table sx={{ minWidth: 1040 }}>
            <TableHead>
              <TableRow>
                <StyledHeaderCell>Created</StyledHeaderCell>
                <StyledHeaderCell>Requestor</StyledHeaderCell>
                <StyledHeaderCell>Phone</StyledHeaderCell>
                <StyledHeaderCell>Service</StyledHeaderCell>
                <StyledHeaderCell align="right">Amount</StyledHeaderCell>
                <StyledHeaderCell>Status</StyledHeaderCell>
                <StyledHeaderCell>Payment</StyledHeaderCell>
                <StyledHeaderCell>Got as far as</StyledHeaderCell>
                <StyledHeaderCell>Valet</StyledHeaderCell>
                <StyledHeaderCell align="right">Valets alerted</StyledHeaderCell>
                <StyledHeaderCell>Address</StyledHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => (
                <TableRow key={r._id} hover>
                  <StyledCell sx={{ whiteSpace: 'nowrap', color: VALET.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtWhen(r.createdAt)}
                  </StyledCell>
                  <StyledCell sx={{ fontWeight: 600 }}>{r.customerName}</StyledCell>
                  <StyledCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: VALET.textMuted }}>
                    {r.customerPhone}
                  </StyledCell>
                  <StyledCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.service}
                    {r.isFreeService ? (
                      <Pill tone="flat" sx={{ marginLeft: '6px' }}>free</Pill>
                    ) : null}
                  </StyledCell>
                  <StyledCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {r.amount ? `$${r.amount.toFixed(2)}` : '—'}
                  </StyledCell>
                  <StyledCell>
                    <Pill tone={statusTone(r.status)}>{r.status}</Pill>
                  </StyledCell>
                  <StyledCell>
                    <Pill tone={r.paymentStatus === 'paid' ? 'good' : 'bad'}>{r.paymentStatus}</Pill>
                  </StyledCell>
                  <StyledCell>
                    {r.reached ? <Pill tone={reachedTone(r.reached)}>{r.reached}</Pill> : '—'}
                  </StyledCell>
                  <StyledCell sx={{ whiteSpace: 'nowrap', color: VALET.textMuted }}>
                    {r.valetName || '—'}
                  </StyledCell>
                  <StyledCell
                    align="right"
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 700,
                      color: r.valetsPaged ? VALET.textPrimary : VALET.danger,
                    }}
                  >
                    {r.valetsPaged}
                  </StyledCell>
                  <StyledCell sx={{ color: VALET.textMuted, maxWidth: '240px' }}>
                    {r.address || '—'}
                  </StyledCell>
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow>
                  <StyledCell colSpan={11} sx={{ textAlign: 'center', color: VALET.textMuted, padding: '32px' }}>
                    No requests match that.
                  </StyledCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Scroller>
        )}
      </TableWrapper>

      <Caption sx={{ marginTop: '12px' }}>
        Showing {visible.length} of {counts.all}. “Valets alerted” is how many valets’ phones buzzed with the job — 0 means nobody was told.
      </Caption>
    </Box>
  );
}
